// Priority+ fit computation for the adaptive contract tab bar. Given the
// measured pixel widths of every tab plus the More button, decide which tabs
// fit in the visible row and which collapse into the overflow row.
//
// Two entry points:
//   - splitTabFit: used while the overflow row is CLOSED. If the active tab
//     would be hidden, it is pulled forward into the visible row (width-aware:
//     the visible prefix shrinks from its tail until the active tab fits),
//     so the selected tab is always on screen.
//   - splitTabFitCanonical: used while the overflow row is OPEN. No pull-forward
//     — the canonical split renders so a just-clicked overflow tab does not jump
//     between rows.
//
// The all-fit check runs BEFORE reserving space for the More button. That makes
// the visible/overflow decision a deterministic step function of container
// width (the whole row either fits or it doesn't), which is the hysteresis that
// prevents boundary oscillation — no debounce needed.

export interface SplitTabFitInput {
  /** Pixel width of every tab, in editorial order. */
  itemWidths: number[];
  /** Pixel width of the More button (reserved only when the row overflows). */
  moreWidth: number;
  /** Available pixel width of the tab-bar container. */
  containerWidth: number;
  /** Pixel gap between adjacent items (the flex `gap`). */
  gap: number;
  /** Index of the active tab. */
  activeIndex: number;
  /** Slack (px) so sub-pixel rounding never tips an exactly-fitting row into
   *  overflow. Defaults to 8. */
  buffer?: number;
}

export interface SplitTabFitResult {
  visibleIndices: number[];
  overflowIndices: number[];
  /** True when the active tab was swapped into the visible row because the
   *  canonical split would have hidden it (closed-row behaviour only). */
  pulledForward: boolean;
}

/** Width of a contiguous run of `count` items including the gaps between them. */
function rowWidth(itemWidths: number[], count: number, gap: number): number {
  if (count <= 0) return 0;
  let total = 0;
  for (let i = 0; i < count; i++) total += itemWidths[i];
  return total + (count - 1) * gap;
}

/** Greedy count of leading items that fit within `available`, clamped to >= 1
 *  so at least one tab is always shown. */
function fittingPrefixCount(
  itemWidths: number[],
  available: number,
  gap: number,
): number {
  const n = itemWidths.length;
  let count = 0;
  let used = 0;
  for (let i = 0; i < n; i++) {
    const next = used + (count === 0 ? 0 : gap) + itemWidths[i];
    if (next > available) break;
    used = next;
    count += 1;
  }
  return Math.max(1, count);
}

function canonicalSplit(input: SplitTabFitInput): {
  visibleCount: number;
  allFit: boolean;
} {
  const { itemWidths, moreWidth, containerWidth, gap, buffer = 8 } = input;
  const n = itemWidths.length;

  // All-fit check first (no More reserved): deterministic step function.
  if (rowWidth(itemWidths, n, gap) + buffer <= containerWidth) {
    return { visibleCount: n, allFit: true };
  }

  const available = containerWidth - moreWidth - buffer;
  const visibleCount = fittingPrefixCount(itemWidths, available, gap);
  return { visibleCount, allFit: false };
}

function toResult(
  visibleIndices: number[],
  overflowIndices: number[],
  pulledForward: boolean,
): SplitTabFitResult {
  return { visibleIndices, overflowIndices, pulledForward };
}

/** Canonical split with no active-tab pull-forward (open-row behaviour). */
export function splitTabFitCanonical(input: SplitTabFitInput): SplitTabFitResult {
  const n = input.itemWidths.length;
  const { visibleCount } = canonicalSplit(input);
  const visibleIndices = Array.from({ length: visibleCount }, (_, i) => i);
  const overflowIndices = Array.from(
    { length: n - visibleCount },
    (_, i) => visibleCount + i,
  );
  return toResult(visibleIndices, overflowIndices, false);
}

/** Split with width-aware active-tab pull-forward (closed-row behaviour). */
export function splitTabFit(input: SplitTabFitInput): SplitTabFitResult {
  const { itemWidths, moreWidth, containerWidth, gap, activeIndex, buffer = 8 } =
    input;
  const n = itemWidths.length;
  const { visibleCount, allFit } = canonicalSplit(input);

  // Active tab is already in the canonical visible prefix, or everything fits.
  if (allFit || activeIndex < visibleCount) {
    return splitTabFitCanonical(input);
  }

  // The active tab is hidden. Shrink the visible prefix from its tail until the
  // active tab fits alongside the surviving prefix (plus the reserved More).
  const available = containerWidth - moreWidth - buffer;
  let prefixCount = visibleCount;
  while (prefixCount > 0) {
    // Width of the surviving prefix (indices 0..prefixCount-1) plus the active
    // tab appended after a gap.
    const prefix = rowWidth(itemWidths, prefixCount, gap);
    const withActive = prefix + gap + itemWidths[activeIndex];
    if (withActive <= available) break;
    prefixCount -= 1;
  }
  // Always keep at least the active tab on screen.
  prefixCount = Math.max(0, prefixCount);

  const visibleIndices = [
    ...Array.from({ length: prefixCount }, (_, i) => i),
    activeIndex,
  ];
  const visibleSet = new Set(visibleIndices);
  const overflowIndices: number[] = [];
  for (let i = 0; i < n; i++) {
    if (!visibleSet.has(i)) overflowIndices.push(i);
  }
  return toResult(visibleIndices, overflowIndices, true);
}
